import React, { useState } from 'react';
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import type { LegacyAuditTableProps } from './types';
import LegacyAuditTableRow from './LegacyAuditTableRow';
import LegacyAuditTableHeader from './LegacyAuditTableHeader';
import LegacyReconciliationDialog from './LegacyReconciliationDialog';
import type { DailyAuditSummary } from '../../services/storeAuditReportsService';
import { getLegacyAuditTableColumnCount } from './utils/legacyAuditStoreConfiguration';

const LegacyAuditTable: React.FC<LegacyAuditTableProps> = ({ rows, store, loading }) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState<DailyAuditSummary | null>(null);

  const handleExpandClick = (rowIdx: number) => {
    setExpandedRows((prev) =>
      prev.includes(rowIdx) ? prev.filter((idx) => idx !== rowIdx) : [...prev, rowIdx]
    );
  };

  const handleOpenReconcile = (summary: DailyAuditSummary) => {
    setSelectedSummary(summary);
    setReconcileDialogOpen(true);
  };

  const handleCloseReconcile = () => {
    setReconcileDialogOpen(false);
  };

  // Filter and sort audit summaries
  const processedRows = React.useMemo(() => {
    if (!rows || rows.length === 0) {
      return [];
    }

    // Filter out rows with no activity
    const filteredRows = rows.filter((row: any) => {
      const posScratcherQuantity = Number(row?.daily_totals?.pos?.scratcher_count ?? 0);
      const totalSold = Number(row?.daily_totals?.audit?.total_sold ?? 0);
      const posScratcherSales = Number(row?.daily_totals?.pos?.scratcher_sales ?? 0);
      const revenue = Number(row?.daily_totals?.audit?.total_revenue ?? 0);
      const posLottoSales = Number(row?.daily_totals?.pos?.lotto_sales ?? 0);
      const lottoSales = Number(row?.daily_totals?.lotto?.sales ?? 0);
      const lottoPayoutsTotal = Number(row?.daily_totals?.lotto?.payouts_total ?? 0);
      const freeTicketsAmount = Number(row?.daily_totals?.lotto?.scratcher_free_tickets_paid_amount ?? 0);
      const posPayout = Number(row?.daily_totals?.pos?.payout ?? 0);
      const movedToBackstockCount = Number(row?.daily_totals?.audit?.moved_to_backstock_count ?? 0);
      const auditCount = row?.audits?.length ?? 0;

      // Check if there are any audits or any activity values
      const hasActivity = posScratcherQuantity !== 0 || totalSold !== 0 || posScratcherSales !== 0 || 
        revenue !== 0 || posLottoSales !== 0 || lottoSales !== 0 || lottoPayoutsTotal !== 0 || 
        freeTicketsAmount !== 0 || posPayout !== 0 || movedToBackstockCount !== 0 || auditCount !== 0;

      return hasActivity;
    });

    // Sort filtered data in ascending order by date
    const sortedRows = filteredRows.sort((a: any, b: any) => {
      const dateA = new Date(a?.report_date);
      const dateB = new Date(b?.report_date);
      return dateA.getTime() - dateB.getTime(); // Ascending order (earliest date first)
    });

    return sortedRows;
  }, [rows]);
  const tableColumnCount = React.useMemo(() => getLegacyAuditTableColumnCount(store), [store]);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead
            sx={{
              backgroundColor: '#F3F2F7',
              '& .MuiTableCell-root': {
                backgroundColor: '#F3F2F7',
                borderBottom: '1px solid #A3AED0CC',
              },
            }}
          >
            <LegacyAuditTableHeader selectedStore={store} />
          </TableHead>
          <TableBody sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
            },
            '& .MuiTableRow-root': {
              borderBottom: 'none',
            },
          }}>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount}>
                  <Typography component="div" sx={{ py: 4, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Typography>
                </TableCell>
              </TableRow>
            ) : processedRows.length > 0 ? (
              processedRows.map((summary, rowIdx) => (
                <LegacyAuditTableRow
                  key={summary.report_date}
                  summary={summary}
                  rowIdx={rowIdx}
                  isExpanded={expandedRows.includes(rowIdx)}
                  onExpandClick={handleExpandClick}
                  selectedStore={store}
                  handleOpenReconcile={handleOpenReconcile}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableColumnCount}>
                  <Typography component="div" sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    No audit data found for this range.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <LegacyReconciliationDialog
        open={reconcileDialogOpen}
        onClose={handleCloseReconcile}
        summary={selectedSummary}
        store={store}
      />
    </>
  );
};

export default LegacyAuditTable;
